import React from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { actions } from './membersReducer';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const ROLE_LABEL = {
  account_admin: 'Admin',
  account_member: 'Member',
  account_viewer: 'Viewer',
};

const MembersContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const { items, isLoading, error, pendingUserId } = useSelector(
    (state) => state.guardian.members
  );
  const me = useSelector((state) => state.guardian.auth.user);
  const myRole = useSelector((state) => state.guardian.auth.role);
  const canEdit = myRole === 'account_admin';

  useEffect(() => {
    dispatch(actions.fetchMembers());
  }, [dispatch]);

  const handleChange = (userId, newRole) => {
    if (!canEdit) return;
    dispatch(actions.changeRole(userId, newRole));
  };

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack
        sx={{
          gap: tokens.aliasesSpaceComponentDense,
          marginBottom: tokens.aliasesSpaceContainerDefault,
        }}
      >
        <Typography variant="h2">Members</Typography>
        <Typography variant="captionSubtle">
          Users with access to this account.{' '}
          {canEdit
            ? "Change a member's role using the dropdown."
            : 'You need account admin to change roles.'}
        </Typography>
      </Stack>

      {error && (
        <Typography
          variant="body1"
          sx={{
            color: tokens.aliasesColorTextError,
            marginBottom: tokens.aliasesSpaceContainerDefault,
            cursor: 'pointer',
          }}
          onClick={() => dispatch(actions.clearError())}
        >
          {error} (click to dismiss)
        </Typography>
      )}

      {isLoading && items.length === 0 ? (
        <Typography variant="body1">Loading…</Typography>
      ) : (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: tokens.aliasesColorBgStaticContainerSubtle,
            '& th, & td': {
              padding: tokens.aliasesSpaceContainerDefault,
              borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
              textAlign: 'left',
            },
          }}
        >
          <thead>
            <tr>
              <th><Typography variant="body1Bold">Email</Typography></th>
              <th><Typography variant="body1Bold">Name</Typography></th>
              <th><Typography variant="body1Bold">Role</Typography></th>
              <th><Typography variant="body1Bold">Last login</Typography></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const isSelf = me && me.id === m.user_id;
              const isPending = pendingUserId === m.user_id;
              return (
                <tr key={m.user_id}>
                  <td>
                    <Typography variant="body1">{m.username}</Typography>
                  </td>
                  <td><Typography variant="body1">{m.name || '—'}</Typography></td>
                  <td>
                    <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDense }}>
                      {canEdit && !isSelf ? (
                        <TextField
                          select
                          value={m.role}
                          onChange={(e) => handleChange(m.user_id, e.target.value)}
                          disabled={isPending}
                          bdsSize="small"
                          sx={{ minWidth: 140 }}
                        >
                          <MenuItem value="account_admin">Admin</MenuItem>
                          <MenuItem value="account_member">Member</MenuItem>
                          <MenuItem value="account_viewer">Viewer</MenuItem>
                        </TextField>
                      ) : (
                        <Chip
                          label={ROLE_LABEL[m.role] || m.role}
                          bdsType={m.role === 'account_admin' ? 'staticEmphasis' : 'staticNeutral'}
                          bdsSize="medium"
                        />
                      )}
                      {isSelf && (
                        <Typography variant="captionSubtle">(you)</Typography>
                      )}
                    </Stack>
                  </td>
                  <td><Typography variant="captionSubtle">{fmtDate(m.last_login)}</Typography></td>
                </tr>
              );
            })}
            {items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={4}>
                  <Typography variant="captionSubtle">No members yet.</Typography>
                </td>
              </tr>
            )}
          </tbody>
        </Box>
      )}
    </Box>
  );
};

export default MembersContainer;
